import type { CSSProperties } from 'react';

// Shared, always-visible helper stating exactly which permissions a GitHub
// Personal Access Token needs, so customers scope it correctly the first time and
// don't hit a repository-creation failure from an under-scoped token. Rendered on
// EVERY PAT-entry surface (settings GitHub-connection card, onboarding wizard
// infrastructure step, managed-onboarding form) — a single source of truth so the
// stated requirements never drift across surfaces, or from what repo provisioning
// actually needs.
//
// Informational only: this does NOT pre-gate anything. The provisioning operation
// remains the arbiter — a correctly-scoped token works; an under-scoped one fails
// with GitHub's real permission error.
//
// Permission mapping (what create_new actually does):
//   - Classic: `repo` (create repo + push contents + branch protection) and
//     `workflow` (push .github/workflows/* — gated separately by GitHub).
//   - Fine-grained:
//       Administration: Read & write  → repository creation + branch protection
//       Contents:       Read & write  → push the starter/template files
//       Workflows:      Read & write  → push the CI workflow (.github/workflows/*),
//                                       a distinct permission from Contents
//       Metadata:       Read          → mandatory baseline (auto-included)
//     and Repository access must be "All repositories" — a token limited to
//     "Only select repositories" cannot create a NEW repo.

const CLASSIC_PAT_URL      = 'https://github.com/settings/tokens/new?scopes=repo,workflow&description=BluePrint';
const FINE_GRAINED_PAT_URL = 'https://github.com/settings/personal-access-tokens/new';

const linkStyle: CSSProperties = { color: 'var(--primary)', textDecoration: 'underline' };
const codeStyle: CSSProperties = { fontFamily: 'var(--font-mono, monospace)' };

interface Props {
  /** Optional style overrides (e.g. spacing) for the host surface. */
  style?: CSSProperties;
  /** Optional DOM id — used to keep a host input's aria-describedby resolvable. */
  id?: string;
}

export default function GithubPatPermissionsHelp({ style, id }: Props) {
  return (
    <div
      id={id}
      data-testid="github-pat-permissions-help"
      style={{
        marginTop:    8,
        padding:      '10px 12px',
        background:   'rgba(124,58,237,0.04)',
        border:       '1px solid rgba(124,58,237,0.15)',
        borderRadius: 'var(--radius-sm)',
        fontFamily:   'var(--font-body)',
        fontSize:     12,
        lineHeight:   1.5,
        color:        'var(--text-secondary)',
        ...style,
      }}
    >
      <p style={{ margin: '0 0 8px', fontWeight: 600, color: 'var(--text)' }}>
        Your GitHub token needs these permissions
      </p>

      {/* Classic PAT */}
      <p style={{ margin: '0 0 2px' }}>
        <strong>Classic token</strong> — scopes <code style={codeStyle}>repo</code> and{' '}
        <code style={codeStyle}>workflow</code>.{' '}
        <a href={CLASSIC_PAT_URL} target="_blank" rel="noreferrer noopener" style={linkStyle}>
          Create a classic token →
        </a>
      </p>

      {/* Fine-grained PAT */}
      <p style={{ margin: '8px 0 2px' }}><strong>Fine-grained token</strong>:</p>
      <ul style={{ margin: '0 0 6px', paddingLeft: 18 }}>
        <li><strong>Repository access:</strong> All repositories — not “Only select repositories”, or it can’t create a new repo</li>
        <li><strong>Administration:</strong> Read and write — repository creation &amp; branch protection</li>
        <li><strong>Contents:</strong> Read and write — push the starter files</li>
        <li><strong>Workflows:</strong> Read and write — push the CI workflow</li>
        <li><strong>Metadata:</strong> Read — required (included automatically)</li>
      </ul>
      <a href={FINE_GRAINED_PAT_URL} target="_blank" rel="noreferrer noopener" style={linkStyle}>
        Create a fine-grained token →
      </a>
    </div>
  );
}
